<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useMutation } from '@tanstack/vue-query'
import { toast, ToastTypes } from '@/helpers/toast'

import UiButton from '@/components/shared/UiButton.vue'
import UiPanel from '@/components/shared/UiPanel.vue'
import UiPanelWrapper from '@/components/shared/UiPanelWrapper.vue'

import BetIcon from '@/assets/icons/bet.svg?component'
import TableIcon from '@/assets/icons/table.svg?component'

import PlayersPanelCard from './PlayersPanelCard.vue'
import PlayersPanelCardEditStack from './PlayersPanelCardEditStack.vue'

import { useUiState } from '@/stores/uiState'
import { useGameState } from '@/stores/gameState'

import { updatePlayer } from '@/api/table/updatePlayer'
import { removeBot } from '@/api/table/removeBot'
import { transferOwnership } from '@/api/table/transferOwnership'

const { editable } = defineProps<{
  editable?: boolean
}>()

const { t } = useI18n()

const { roomId, roomState, player, getMarkBotAsRemoving } = storeToRefs(useGameState())
const { getPlayerById, markBotAsRemoving, clearBotAsRemoving } = useGameState()
const {
  panels: { playersPanel }
} = useUiState()

const joinRequests = computed(() => roomState?.value?.joinRequests ?? [])

const { mutate: updatePlayerMutation, isPending: isUpdatingBot } = useMutation({
  mutationFn: updatePlayer,
  onSuccess(data, variables) {
    toast({
      message: t('Compete.message.StackUpdated'),
      details: variables.isBot
        ? t('Compete.message.BotStackUpdated')
        : t('Compete.message.PlayerStackUpdated'),
      icon: ToastTypes.SUCCESS
    })
  },
  onError(error) {
    toast({
      message: t('Errors.label.Oops'),
      details: error.message,
      icon: ToastTypes.ERROR
    })
  }
})
const { mutate: removeBotMutation, isPending: isRemovingBot } = useMutation({
  mutationFn: removeBot,
  onSuccess() {
    toast({
      message: t('Compete.message.BotRemoved'),
      details: t('Compete.message.BotRemovedDetails'),
      icon: ToastTypes.SUCCESS
    })
  },
  onError(error) {
    toast({
      message: t('Errors.label.Oops'),
      details: error.message,
      icon: ToastTypes.ERROR
    })
  }
})
const { mutate: transferOwnershipMutation, isPending: isTransferingOwnership } = useMutation({
  mutationFn: transferOwnership,
  onSuccess() {
    toast({
      message: t('Compete.message.OwnershipTransfered'),
      details: t('Compete.message.OwnershipTransferedDetails'),
      icon: ToastTypes.SUCCESS
    })
  },
  onError(error) {
    toast({
      message: t('Errors.label.Oops'),
      details: error.message,
      icon: ToastTypes.ERROR
    })
  }
})

const handleRemoveBot = (botId: string) => {
  markBotAsRemoving(botId)
  removeBotMutation(
    { tableId: roomId.value!, botId: botId },
    {
      onError: () => {
        clearBotAsRemoving(botId)
      }
    }
  )
}

const checkBotForRemovingList = (botId: string) => {
  return getMarkBotAsRemoving.value.includes(botId)
}

function approveSeatRequest(data) {
  console.log('approveSeatRequest', data)
}
function rejectSeatRequest(data) {
  console.log('rejectSeatRequest', data)
}
function removePlayer(data) {
  console.log('removePlayer', data)
}

const isBusy = computed(
  () => isUpdatingBot.value || isRemovingBot.value || isTransferingOwnership.value
  // isApproving.value ||
  // isRejecting.value ||
  // isRemoving.value ||
  // isUpdatingStack.value ||
)

const seats = computed(() =>
  Object.entries(roomState?.value?.seats ?? [])
    .filter(([, value]) => Boolean(value))
    .map(([index, seat]) => ({ seatIndex: index, player: getPlayerById(seat.playerId) }))
)
const playersList = computed(() => seats.value.filter((el) => !el.player.bot))
const botsList = computed(() => seats.value.filter((el) => el.player.bot))
</script>
<template>
  <ui-panel-wrapper drawer :open="playersPanel.isOpen" @close="playersPanel.close()">
    <ui-panel
      class="players-menu z-9999 px-5! py-4!"
      :title="$t('Compete.label.Players')"
      :show-back-label="false"
      @close="playersPanel.close()"
    >
      <section class="players-menu__section" v-if="joinRequests?.length && editable">
        <div class="players-menu__section-title">
          {{ $t('Compete.label.Requests') }}
          <span class="players-menu__section-count">{{ joinRequests?.length }}</span>
        </div>
        <ul class="players-menu__list">
          <li v-for="request in joinRequests" :key="request.playerId">
            <players-panel-card :player="getPlayerById(request.playerId)">
              <template #extra>
                <span class="players-menu__request-ingress">
                  {{ $t('Compete.label.RequestIngress') }}
                </span>
                {{ $t('Compete.label.IntendedSeat') }}
                <table-icon class="players-menu__table-icon" />
                <span class="players-menu__seat">#{{ request.seatIndex + 1 }}</span>
              </template>
              <players-panel-card-edit-stack
                :index="request.playerId"
                :stack="request.stack"
                open-only
                @submit="
                  (value) => approveSeatRequest({ playerId: request.playerId, stack: value })
                "
                @cancel="rejectSeatRequest({ playerId: request.playerId })"
              />
            </players-panel-card>
          </li>
        </ul>
      </section>
      <section class="players-menu__section">
        <div class="players-menu__section-title">{{ $t('Compete.label.Bots') }}</div>
        <ul v-if="botsList.length" class="players-menu__list">
          <li v-for="seat in botsList" :key="seat.seatIndex">
            <players-panel-card v-if="seat" :player="seat.player">
              <template #extra>
                {{ $t('Compete.label.Stack') }} <bet-icon class="players-menu__table-icon" />
                <div class="players-menu__stack">{{ seat.player.stack }}</div>
                {{ $t('Compete.label.IntendedSeat') }}
                <table-icon class="players-menu__table-icon"></table-icon>
                <span class="players-menu__seat">#{{ Number(seat.seatIndex) + 1 }}</span>
              </template>
              <players-panel-card-edit-stack
                :index="seat.seatIndex"
                v-if="editable"
                :is-bot="seat.player.bot"
                :stack="seat.player.stack"
                :disabled="checkBotForRemovingList(seat.player.playerId)"
                @submit="
                  (value) =>
                    updatePlayerMutation({
                      type: 'host',
                      tableId: roomId!,
                      playerId: seat.player.playerId,
                      stack: value,
                      isBot: true
                    })
                "
                @remove-bot="handleRemoveBot(seat.player.playerId)"
              />
            </players-panel-card>
          </li>
        </ul>
        <div v-else>{{ $t('Compete.placeholder.NoBotsConnected') }}</div>

        <!-- TODO: AddMoreAIBots from panel -->
        <!-- <ui-button v-if="editable" variant="secondary" class="w-full rounded-full!" disabled>{{
          $t('Compete.button.AddMoreAIBots')
        }}</ui-button> -->
      </section>
      <section class="players-menu__section">
        <div class="players-menu__section-title">{{ $t('Compete.label.ActivePlayers') }}</div>
        <ul class="players-menu__list" v-if="playersList.length">
          <li v-for="seat in playersList" :key="seat.seatIndex">
            <players-panel-card
              v-if="seat"
              :player="seat.player"
              :isYou="player?.playerId === seat.player.playerId"
              :isHost="roomState.hostId === seat.player.playerId"
            >
              <template #extra>
                {{ $t('Compete.label.Stack') }} <bet-icon class="players-menu__table-icon" />
                <div class="players-menu__stack">{{ seat.player.stack }}</div>
                {{ $t('Compete.label.IntendedSeat') }}
                <table-icon class="players-menu__table-icon"></table-icon>
                <span class="players-menu__seat">#{{ Number(seat.seatIndex) + 1 }}</span>
              </template>
              <players-panel-card-edit-stack
                v-if="editable"
                :index="seat.seatIndex"
                :stack="seat.player.stack"
                @submit="
                  (value) =>
                    updatePlayerMutation({
                      type: 'host',
                      tableId: roomId!,
                      playerId: seat.player.playerId,
                      stack: value,
                      isBot: false
                    })
                "
              />

              <div v-if="editable" class="players-menu__actions w-full!">
                <ui-button
                  v-if="player?.playerId !== seat.player.playerId"
                  variant="secondary"
                  class="players-menu__action-button w-full! rounded-full!"
                  @click="
                    transferOwnershipMutation({ tableId: roomId!, playerId: seat.player.playerId })
                  "
                >
                  {{ $t('Compete.button.TransferOwnership') }}
                </ui-button>
              </div>
            </players-panel-card>
          </li>
        </ul>
        <div v-else>{{ $t('Compete.placeholder.NoPlayersConnected') }}</div>
      </section>
    </ui-panel>
  </ui-panel-wrapper>
</template>

<style lang="scss">
.players-menu {
  position: absolute;

  top: 64px;
  right: 24px;

  --btns-bottom-gap: min(20px, 10vh);
  --btns-top-gap: 1rem;
  --btns-h: 65px;
  --btns-slider-h: 80px;
  bottom: calc(var(--btns-bottom-gap) + var(--btns-top-gap) + var(--btns-h) + var(--btns-slider-h));

  width: 420px;

  &__section {
    @apply mx-1 flex flex-col gap-3;
  }

  &__section-title {
    @apply text-xs font-medium text-white;
  }

  &__section-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: #e65c5c;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    @apply flex flex-col gap-2;
  }

  &__actions {
    display: flex;
    gap: 4px;
  }

  &__additional-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__actions-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-self: stretch;
  }

  &__input-label {
    width: 100%;
  }

  &__input {
    flex: 1;
  }

  &__action-button {
    flex-shrink: 0;
    width: 78px;
  }

  &__table-icon {
    margin: 0 4px;
  }

  &__request-ingress {
    display: inline-flex;
    align-items: center;
    color: #fff;
    margin-right: 12px;

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      margin-right: 4px;
      border-radius: 50%;
      background-color: #e65c5c;
    }
  }

  &__stack {
    color: #fff;
    margin-right: 12px;
  }

  &__edit-button {
    width: 100%;
    font-weight: 500;
  }

  &__action-title {
    margin-top: 12px;
    color: #86899e;
    font-family: Poppins;
    font-size: 12px;
    font-weight: 500;
  }

  &__seat {
    color: #fff;
  }
}

@media (max-height: 768px) {
  .players-menu {
    --btns-slider-h: 38px;
  }
}

@media (max-height: 768px) {
  .players-menu {
    --btns-slider-h: 38px;
  }
}
</style>
